import { tool } from "ai";
import { z } from "zod";

import {
  AIRLINE_CODE_PATTERN,
  MAX_AIRLINE_PICKS,
  MAX_CANDIDATE_CITIES,
  MAX_CITY_SUGGESTIONS,
  MAX_DATE_WINDOWS,
  MAX_DESTINATIONS,
  MAX_ORIGIN_COUNT,
  MAX_REPLY_SUGGESTIONS,
} from "./constants";

const CITY = z.string().trim().min(1).max(80);
const DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
const RANGE = z
  .object({ low: z.number().min(0), high: z.number().min(0) })
  .refine((range) => range.high >= range.low, "high must be at least low")
  .describe("Amount in the estimate's display currency, per person.");

/** Input schemas for every estimator chat tool, shared by the route and the client that applies them. */
export const estimatorToolSchemas = {
  // Models sometimes nest the list one level too deep ({ origins: { origins: [...] } }); accept both.
  set_origins: z.preprocess(
    (value) => {
      const inner = (value as { origins?: { origins?: unknown } } | null)?.origins;
      return inner && typeof inner === "object" && !Array.isArray(inner) && "origins" in inner ? inner : value;
    },
    z
      .object({
        origins: z
          .array(
            z.object({
              city: CITY,
              count: z.number().int().min(1).max(MAX_ORIGIN_COUNT),
              group: z.string().trim().min(1).max(60).optional().describe("Team name from the org's teams, if any."),
              cabin: z.enum(["economy", "premium economy", "business", "first"]).optional(),
            })
          )
          .max(100)
          .describe(
            "Traveler groups: home city, how many fly from it, optionally its team and cabin (e.g. Leadership in business). Empty only with replace: true, to clear every home city."
          ),
        replace: z
          .boolean()
          .default(false)
          .describe(
            "True only when these groups are everyone traveling and every other home city should go. False (the default) adds these groups or changes their counts and keeps the rest: 'make it 40 from San Diego' changes San Diego only."
          ),
      })
      .refine((input) => input.origins.length > 0 || input.replace, {
        message: "Name at least one traveler group, or pass replace: true to clear every home city.",
      })
  ),
  add_destination: z.object({ city: CITY.describe("Destination city name, e.g. 'Porto'.") }),
  remove_destination: z.object({ city: CITY }),
  set_dates: z.object({
    start: DATE.describe("Arrival date."),
    end: DATE.describe("Departure date. Nights are end minus start."),
  }),
  set_assumptions: z.object({
    meetingSpacePerDay: RANGE.optional(),
    mealsPerDay: RANGE.optional().describe("Meals and activities per person per day, display currency."),
  }),
  set_trip_details: z.object({
    title: z.string().trim().min(1).max(80).optional(),
    cabin: z.enum(["economy", "premium economy", "business", "first"]).optional(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional()
      .describe("ISO 4217 code to show the estimate in, e.g. GBP."),
    headcount: z
      .number()
      .int()
      .min(1)
      .max(MAX_ORIGIN_COUNT)
      .optional()
      .describe(
        "Total people in the group, including anyone without a home city. With home cities set, the people beyond them count as location unknown."
      ),
  }),
  set_airlines: z.object({
    mode: z
      .enum(["any", "only", "exclude"])
      .describe("any: every airline (Spirit and Frontier stay out). only: just these. exclude: never these."),
    airlines: z
      .array(
        z.object({
          code: z.string().trim().toUpperCase().regex(AIRLINE_CODE_PATTERN).describe("IATA airline code, e.g. UA."),
          name: z.string().trim().min(1).max(80).describe("Airline name, e.g. United Airlines."),
        })
      )
      .max(MAX_AIRLINE_PICKS)
      .default([])
      .describe("The airlines for only or exclude; empty for any."),
  }),
  suggest_nearby_cities: z.object({
    cities: z
      .array(z.object({ city: CITY, reason: z.string().max(120) }))
      .min(1)
      .max(MAX_CITY_SUGGESTIONS),
  }),
  suggest_dates: z.object({
    options: z
      .array(
        z.object({
          start: DATE,
          end: DATE,
          label: z.string().min(1).max(40).describe("Short button text, e.g. 'Mar 3 – 5'."),
          reason: z.string().max(120).optional(),
        })
      )
      .min(1)
      .max(MAX_REPLY_SUGGESTIONS),
  }),
  suggest_replies: z.object({
    replies: z
      .array(z.string().min(1).max(60))
      .min(1)
      .max(MAX_REPLY_SUGGESTIONS)
      .describe("Short next steps the planner can tap, written as the planner would say them."),
  }),
  compare_date_windows: z.object({
    windows: z
      .array(z.object({ start: DATE, end: DATE, label: z.string().min(1).max(40), reason: z.string().max(120) }))
      .min(2)
      .max(MAX_DATE_WINDOWS)
      .describe("Candidate trips of the same length, e.g. midweek and weekend options over the next 8 weeks."),
  }),
  suggest_destinations: z.object({
    candidates: z
      .array(z.object({ city: CITY, reason: z.string().max(120) }))
      .min(2)
      .max(MAX_CANDIDATE_CITIES)
      .describe("Cities worth comparing for these home cities: easy hubs for everyone plus cheaper options."),
    add: z.number().int().min(1).max(MAX_DESTINATIONS).default(3).describe("How many of the cheapest to add."),
  }),
  load_event: z.object({ event_id: z.number().int().positive().describe("Id of one of the org's active events.") }),
  read_estimate: z.object({}),
} as const;

/** Name of an estimator chat tool. */
export type EstimatorToolName = keyof typeof estimatorToolSchemas;

/** Parsed input of one estimator chat tool. */
export type EstimatorToolInput<Name extends EstimatorToolName> = z.infer<(typeof estimatorToolSchemas)[Name]>;

/**
 * The estimator chat's tools. None has `execute`: the browser applies each one to the
 * workspace and reports the result, so the workspace stays the single source of truth.
 */
export const estimatorTools = {
  set_origins: tool({
    description: "Set the traveler groups: home cities, counts, and optionally team and cabin per group.",
    inputSchema: estimatorToolSchemas.set_origins,
  }),
  add_destination: tool({
    description: `Add a destination city to the comparison (at most ${MAX_DESTINATIONS}).`,
    inputSchema: estimatorToolSchemas.add_destination,
  }),
  remove_destination: tool({
    description: "Remove a destination city from the comparison.",
    inputSchema: estimatorToolSchemas.remove_destination,
  }),
  set_dates: tool({ description: "Set the trip dates.", inputSchema: estimatorToolSchemas.set_dates }),
  set_assumptions: tool({
    description: "Change the per-person cost assumptions. Omit a field to leave it unchanged.",
    inputSchema: estimatorToolSchemas.set_assumptions,
  }),
  set_trip_details: tool({
    description:
      "Rename the estimate, change the cabin class or display currency, or set the headcount when home cities are unknown.",
    inputSchema: estimatorToolSchemas.set_trip_details,
  }),
  set_airlines: tool({
    description:
      "Limit fares to some airlines ('only United and Delta'), leave airlines out ('no American'), or go back to any airline.",
    inputSchema: estimatorToolSchemas.set_airlines,
  }),
  suggest_nearby_cities: tool({
    description:
      "Offer the user cities worth comparing as one-tap buttons, without adding them. Use for 'anywhere cheaper?' style questions when the comparison is full or the user should choose.",
    inputSchema: estimatorToolSchemas.suggest_nearby_cities,
  }),
  suggest_dates: tool({
    description:
      "Offer concrete date options as one-tap buttons; tapping one sets the dates. Use whenever dates are missing.",
    inputSchema: estimatorToolSchemas.suggest_dates,
  }),
  suggest_replies: tool({
    description:
      "Offer up to three short follow-ups as one-tap buttons that send that text as the planner's next message.",
    inputSchema: estimatorToolSchemas.suggest_replies,
  }),
  compare_date_windows: tool({
    description:
      "Price 2-4 candidate date windows for the current home cities and destinations, then show them as tappable options with per-person prices. Use when the planner doesn't know or is flexible on dates.",
    inputSchema: estimatorToolSchemas.compare_date_windows,
  }),
  suggest_destinations: tool({
    description:
      "Price candidate destination cities for the current home cities and add the cheapest to the comparison. Use when the planner doesn't know where to go.",
    inputSchema: estimatorToolSchemas.suggest_destinations,
  }),
  load_event: tool({
    description:
      "Fill the estimate from one of the organization's active events: its name, dates, headcount and destinations.",
    inputSchema: estimatorToolSchemas.load_event,
  }),
  read_estimate: tool({
    description:
      "Wait for live fares and hotel rates to finish loading, then return the current comparison. Call after edits whenever your reply depends on the numbers.",
    inputSchema: estimatorToolSchemas.read_estimate,
  }),
};
